import co from 'co'
import axios from 'axios'
import { put, takeLatest, all, call, select, cancelled, delay } from 'redux-saga/effects'

import { strings } from 'context/LanguageContext/strings'
import {
    addLoyaltyIdForWebsite,
    addSubscriptionSuperProp,
    trackRafCredits,
} from 'middleware/helper'
import {
    authLoading,
    getTokensFailed,
    getTokensStart,
    getTokensSuccess,
    loginFailed,
    mfaAuthFailed,
    mfaResendCodeFailed,
    signOut,
} from 'modules/authentication/actions'

import {
    commonFlow,
    parseGetTokens,
    setAddressFromProfileResponse,
} from 'modules/authentication/sagas'
import { updateContactLessOption } from 'modules/basket/sagas'
import { setActiveOrdersCount } from 'modules/orders/actions'
import { getStoresByLocation } from 'modules/location/actions'
import { subscriptionPaymentStatus } from 'modules/subscription/sagas'
import { setScreenError } from 'modules/tokenRefresh/actions'
import {
    actions,
    registerUserAccountSuccess,
    registerUserAccountFailed,
    getUserProfileSuccess,
    getUserProfileFailed,
    setUserProfile,
    updateUserProfileSuccess,
    updateUserProfileFailed,
    getReferralCodeSuccess,
    getReferralCodeFailure,
    ageVerificationSuccess,
    ageVerificationFailed,
    otpGenerateSuccess,
} from 'modules/user/actions'
import {
    createUserProfileURL,
    getUserProfileURL,
    updateUserProfileURL,
    getReferralCodeURL,
    ageVrificationURL,
} from 'modules/user/urls'
import { getSavedCards } from 'modules/wallet/sagas'
import { api } from 'network/api'
import { util } from 'utils'
import { isConversionRequired, isEmpty } from 'utils/appUtils'
import { mfaAuthOTPVerifyRegister } from 'modules/authentication/urls'
import { getPasswordClientID, getPasswordSecretID } from 'network/api/networkSettings'
import { syncingProfile } from './actions'

function* createUserProfile(action) {
    const request = action.payload
    const { authentication } = yield select()

    const auth_user = authentication.toJS()
    const encryptedRequest =
        auth_user.newUserFaceBook && auth_user.newUserFaceBook.first_name
            ? {
                  ...request,
                  email: request.email,
                  access_token: auth_user.newUserFaceBook.accessToken,
                  provider: 'facebook',
              }
            : {
                  ...request,
                  //   email: util.encrypt(request.email),
                  //   password: util.encrypt(request.password),
              }

    const { isConversion, customer_id } = isConversionRequired()
    if (isConversion) {
        encryptedRequest.guest_id = customer_id
    }
    try {
        const response = yield call(api, createUserProfileURL, 'POST', encryptedRequest, 1)
        const { error_message, token, profile } = response
        if (error_message) {
            yield put(registerUserAccountFailed(error_message))
        } else {
            if (!profile) {
                // token is unavailable at the time of registration.
                throw new Error('NO JWT/PROFILE IN CREATE ACC')
            }
            yield put(registerUserAccountSuccess(response))
        }
    } catch (error) {
        yield put(registerUserAccountFailed(strings.createAccountFailure))
    }
}

function* createUserProfileMFA(action) {
    const request = action.payload
    const { authentication } = yield select()

    const auth_user = authentication.toJS()
    const encryptedRequest =
        auth_user.newUserFaceBook && auth_user.newUserFaceBook.first_name
            ? {
                  ...request,
                  email: request.email,
                  access_token: auth_user.newUserFaceBook.accessToken,
                  provider: 'facebook',
              }
            : {
                  ...request,
              }

    const { isConversion, customer_id } = isConversionRequired()
    if (isConversion) {
        encryptedRequest.guest_id = customer_id
    }
    yield put(authLoading(true))
    try {
        const response = yield call(api, createUserProfileURL, 'POST', encryptedRequest, 1)
        const { temp_account_uid, mobile_number, error_message, is_sms_otp_sent } = response
        if (!temp_account_uid) {
            // todo duplicate account
            const message =
                error_message || 'Could not create the account at moment. Please try again'
            yield put(registerUserAccountFailed(message))
            yield put(
                otpGenerateSuccess({
                    data: null,
                    success: false,
                })
            )
        } else {
            yield put(
                otpGenerateSuccess({
                    data: {
                        temp_account_uid,
                        mobile_number,
                        is_sms_otp_sent,
                        email: encryptedRequest.email,
                        guest_id: encryptedRequest.guest_id,
                    },
                    success: true,
                })
            )
        }
    } catch (error) {
        yield put(registerUserAccountFailed(strings.createAccountFailure))
    } finally {
        yield put(authLoading(false))
    }
}

function* mfaVerify(action) {
    try {
        const {
            payload: { temp_account_uid, code, guest_id },
        } = action
        const request = {
            guest_id,
            grant_type: 'password',
        }
        if (code) {
            request['code'] = code
        }
        if (temp_account_uid) {
            request['temp_account_uid'] = temp_account_uid
        }
        if (guest_id) {
            request['guest_id'] = guest_id
        }
        yield put(authLoading(true))
        const otpResponse = yield* mfaVerifyInvoke(request)

        if (otpResponse) {
            const { access_token, refresh_token, error, error_description } = otpResponse
            // temporary fix to handle the error from front-end. Should be fixed from the back-end /verify api
            if (error && error_description && error_description) {
                if (!code) {
                    yield put(
                        mfaResendCodeFailed({
                            data: otpResponse,
                        })
                    )
                } else {
                    yield put(
                        mfaAuthFailed({
                            data: otpResponse,
                        })
                    )
                }
            } else if ((!access_token || !refresh_token) && code) {
                throw new Error('NO TOKENS IN LOGIN')
            } else if (access_token && refresh_token) {
                yield* parseGetTokens(otpResponse)
            }
        } else {
            yield put(loginFailed(strings.loginFailure))
        }
        yield put(authLoading(false))
    } catch (error) {
        console.log('Sigin failed ', error)

        if (!action.code) {
            yield put(
                mfaResendCodeFailed({
                    data: {
                        error_description:
                            error?.message || 'Something went wrong. Please try again',
                    },
                })
            )
        } else {
            yield put(
                mfaAuthFailed({
                    data: {
                        error_description:
                            error?.message || 'Something went wrong. Please try again',
                    },
                })
            )
        }
        yield put(authLoading(false))
    }
}

function* mfaVerifyInvoke(action) {
    const request = {
        ...action,
        client_id: getPasswordClientID(),
        client_secret: getPasswordSecretID(),
    }
    try {
        yield put(getTokensStart())
        const response = yield call(api, mfaAuthOTPVerifyRegister, 'POST', request, 0, 30, false)
        yield put(getTokensSuccess(response))
        return response
    } catch (error) {
        yield put(getTokensFailed(error))
        yield put(
            mfaAuthFailed({
                data: {
                    error_description:
                        error?.data?.message ||
                        error?.message ||
                        'Something went wrong. Please try again',
                },
            })
        )
        console.log(error)
    }
}

export function* getUserProfile() {
    const MAX_RETRIES = 2
    const RETRY_DELAY = 1000
    let retryCount = 0
    let userProfileSuccess = false
    let timeStamp = new Date().getTime()
    const { user } = yield select()
    const { showOTPScreen } = user.toJS() // The showOTPScreen field is taken from the user module and is used only during the registration process.
    const { location } = yield select()
    const selectedAddress = location.get('selectedAddress')?.toJS()

    while (!userProfileSuccess && retryCount < MAX_RETRIES) {
        try {
            const response = yield call(
                api,
                `${getUserProfileURL}?timeStamp=${timeStamp}`,
                'GET',
                null,
                1,
                15
            )
            const { profile } = response

            if (!profile) {
                throw new Error('NO PROFILE IN GET USER PROFILE')
            }
            userProfileSuccess = true
            if (profile && profile.active_orders_count) {
                yield put(setActiveOrdersCount(profile.active_orders_count))
            }
            const { appSettings } = yield select()
            const parsedProfile = util.parseUserProfile(profile)
            if (!isEmpty(parsedProfile)) {
                addLoyaltyIdForWebsite(parsedProfile)
                if (!isEmpty(parsedProfile?.subscriptions)) {
                    addSubscriptionSuperProp(parsedProfile?.subscriptions?.[0])
                }
            }
            yield put(setUserProfile(parsedProfile))
            yield put(getUserProfileSuccess(response))
            if (!showOTPScreen) {
                yield* setAddressFromProfileResponse(response)
            } else {
                yield put(getStoresByLocation(selectedAddress))
            }
        } catch (error) {
            retryCount++
            if (retryCount < MAX_RETRIES) {
                yield delay(RETRY_DELAY)
            } else {
                yield put(getUserProfileFailed(error))
                yield proceedUserProfileFailed(error)
            }
        }
    }
    try {
        if (userProfileSuccess) {
            // yield* getSavedCards()
            yield* commonFlow()
            yield* subscriptionPaymentStatus()
        }
    } catch (error) {
        console.log('error here ==>', error)
    }
}

// expensive request, therefore avoiding duplicate requests
export function* getUserProfileAndSync() {
    const source = axios.CancelToken.source() // if axios is updated ^0.22, change CancelToken for AbortController
    const timeStamp = new Date().getTime()
    try {
        yield put(syncingProfile(true))
        const getAndSyncProfileURL = `${getUserProfileURL}?sync=true?timeStamp=${timeStamp}`
        const response = yield call(
            api,
            getAndSyncProfileURL,
            'GET',
            null,
            1,
            20,
            false,
            null,
            source.token
        )
        const { profile } = response

        if (profile && profile.active_orders_count) {
            yield put(setActiveOrdersCount(profile.active_orders_count))
        }
        const parsedProfile = util.parseUserProfile(profile)
        yield put(setUserProfile(parsedProfile))
    } catch (err) {
        console.log('error while fetching profile ==>', err)
    } finally {
        if (yield cancelled()) {
            source.cancel()
        }
        yield put(syncingProfile(false))
    }
}

export function* proceedUserProfileFailed(error) {
    const { appSettings } = yield select()
    const { isApp } = appSettings ? appSettings.toJS() : {}
    // const { userProfile } = user?.toJS()
    // const { selectedAddress } = location?.toJS()
    if (isApp) {
        yield put(setScreenError(error))
    } else if (error.status >= 500 && error.status <= 599) {
        yield put(signOut())
    }
}

function* updateUserProfile(action) {
    const request = action.payload

    try {
        const response = yield call(api, updateUserProfileURL, 'POST', request)
        const { error_message } = response
        if (error_message) {
            yield put(updateUserProfileFailed({ message: error_message }))
        } else {
            yield put(updateUserProfileSuccess(response))
        }
    } catch (error) {
        yield put(
            updateUserProfileFailed({
                message: strings.genericUpdateProfileFailureDescription,
                status: error && error.status,
            })
        )
    }
}

function* ageVerification(action) {
    const request = action.payload
    try {
        const response = yield call(
            api,
            ageVrificationURL,
            'POST',
            request,
            0,
            10,
            false,
            null,
            null,
            true
        )
        const { error_message } = response
        if (error_message) {
            yield put(ageVerificationFailed({ message: error_message }))
        } else {
            yield put(ageVerificationSuccess(response))
        }
    } catch (error) {
        yield put(
            ageVerificationFailed({
                message:
                    error && error.data ? error.data.error_description : 'Something went wrong',
                status: error && error.status,
            })
        )
    }
}

export const getUserProfileAsync = co.wrap(function* () {
    const timeStamp = new Date().getTime()
    try {
        const res = yield api(`${getUserProfileURL}?timeStamp=${timeStamp}`, 'GET', null, 1, 10)
        return yield res
    } catch (err) {
        return yield err
    }
})

function* noContactToggle() {
    try {
        yield* updateContactLessOption()
    } catch (error) {
        console.log('No contact delivery notes error ', error)
    }
}
function* getReferralCode() {
    try {
        const referAndEarnInfo = yield call(api, getReferralCodeURL, 'GET', null, 1)
        const { referral_codes } = referAndEarnInfo
        const {
            referral_code,
            current_single_credit_value,
            total_available_credits,
            total_available_credit_value,
            country,
            content,
        } = referral_codes[0]
        if (referral_code && current_single_credit_value) {
            const payload = {
                referralCode: referral_code,
                currentSingleCreditValue: current_single_credit_value,
                totalAvailableCredits: total_available_credits,
                totalAvailableCreditValue: total_available_credit_value,
                country,
                faqs: content?.faq,
            }
            trackRafCredits(
                total_available_credit_value && total_available_credit_value >= 1 ? 'Yes' : 'No'
            )
            yield put(getReferralCodeSuccess(payload))
        } else {
            yield put(getReferralCodeFailure())
        }
    } catch (error) {
        yield put(getReferralCodeFailure(error))
    }
}

// function* getFbUserProfile(action) {
//     const token = action.payload

//     try {
//         const fbUserProfile = yield call(fbGraphApi, token, 'email,first_name,last_name')

//         yield all([
//             put(getFbUserProfileSuccess({ ...fbUserProfile, access_token: token })),
//             put(loginWithFbSuccess(null)), // reset loggingInWithFb flag
//         ])
//     } catch (error) {
//         yield put(getFbUserProfileFailed(error))
//     }
// }

// <==== *********************************************** ====>

function* getUserProfileWatcher() {
    yield takeLatest(actions.GET_USER_PROFILE, getUserProfile)
}

function* getUserProfileAndSyncWatcher() {
    yield takeLatest(actions.GET_USER_PROFILE_AND_SYNC, getUserProfileAndSync)
}
// function* getFbUserProfileWatcher() {
//     yield takeLatest(actions.GET_FB_USER_PROFILE, getFbUserProfile)
// }

function* registerUserAccountWatcher() {
    yield takeLatest(actions.REGISTER_USER_ACCOUNT, createUserProfile)
}

function* noContactdeliveryNotesWatcher() {
    yield takeLatest(actions.SET_NO_CONTACT_DROP_OFF, noContactToggle)
}
function* updateUserProfileWatcher() {
    yield takeLatest(actions.UPDATE_USER_PROFILE, updateUserProfile)
}

function* ageVrificationWatcher() {
    yield takeLatest(actions.USER_AGE_VERIFICATION, ageVerification)
}

function* getUserReferralCodeWatcher() {
    yield takeLatest(actions.GET_USER_REFERRAL_CODE, getReferralCode)
}

function* registerUserMFAAccountWatcher() {
    yield takeLatest(actions.REGISTER_USER_ACCOUNT_MFA, createUserProfileMFA)
}

function* mfaVerifyWatcher() {
    yield takeLatest(actions.MFA_VERIFY_REGISTER, mfaVerify)
}

export default function* userAccountSaga() {
    yield all([
        registerUserAccountWatcher(),
        getUserProfileWatcher(),
        noContactdeliveryNotesWatcher(),
        updateUserProfileWatcher(),
        ageVrificationWatcher(),
        getUserReferralCodeWatcher(),
        getUserProfileAndSyncWatcher(),
        registerUserMFAAccountWatcher(),
        mfaVerifyWatcher(),
    ])
}
